import { memo } from "react";
import { SmoothLink as Link } from "@/components/navigation";
import { Badge } from "@/components/ui/badge";
import { User, Star, Video, Building2 } from "lucide-react";
import type { Doctor } from "@/types/schema";
import { cn } from "@/lib/utils";
import { getOptimizedImageUrl } from "@/lib/image-utils";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

interface DoctorCardProps {
  doctor: Doctor;
  className?: string;
  compact?: boolean;
}

const doctorTypeLabels: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  hospital_staff: { label: "Hospital Staff", variant: "secondary" },
  independent: { label: "Independent", variant: "outline" },
  consultant: { label: "Online Consultant", variant: "default" },
};

export const DoctorCard = memo(function DoctorCard({ doctor, className, compact = false }: DoctorCardProps) {
  const { trackDoctorView } = useAnalyticsContext();
  const typeInfo = doctorTypeLabels[doctor.doctor_type] || doctorTypeLabels.hospital_staff;

  return (
    <Link 
      to={`/doctors/${doctor.slug}`}
      onClick={() => trackDoctorView(doctor.name, doctor.specialty)}
    >
      <div className={cn(
        "flex items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border hover:border-primary hover:shadow-sm transition-all bg-card",
        className
      )}>
        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {doctor.image_url ? (
            <img 
              width={100}
              height={100}
              src={getOptimizedImageUrl(doctor.image_url, 100, 100)} 
              alt={doctor.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <User className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-secondary truncate text-sm sm:text-base">
            {doctor.name}
          </h3>
          <p className="text-xs sm:text-sm text-primary truncate">{doctor.title || doctor.specialty}</p>
          
          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1 sm:mt-2">
            <Badge variant={typeInfo.variant} className="text-[10px] sm:text-xs px-1.5 sm:px-2">
              {doctor.doctor_type === 'hospital_staff' && <Building2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />}
              <span className="truncate max-w-[80px] sm:max-w-none">{typeInfo.label}</span>
            </Badge>
            
            {doctor.offers_online_consultation && (
              <Badge variant="outline" className="text-[10px] sm:text-xs bg-accent/10 text-accent border-accent/30 px-1.5 sm:px-2">
                <Video className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
                ${doctor.online_consultation_fee}
              </Badge>
            )}
          </div>
          
          {!compact && (
            <div className="flex items-center gap-2 sm:gap-3 mt-1 sm:mt-2 text-[10px] sm:text-xs text-muted-foreground">
              <span className="flex items-center gap-0.5 sm:gap-1">
                <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-accent text-accent" />
                {doctor.rating}
              </span>
              <span>{doctor.experience_years}+ yrs</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
});
